import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import QuizComponent from '../../components/QuizComponent';
import ScriptureCard from '../../components/ScriptureCard';
import JournalSection from '../../components/JournalSection';
import { Heart, RefreshCw, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const identityTruths = [
  {
    title: 'You Have a New Heart',
    scripture: 'And I will give you a new heart, and a new spirit I will put within you. And I will remove the heart of stone from your flesh and give you a heart of flesh.',
    reference: 'Ezekiel 36:26 ESV',
    explanation: 'God didn\'t just forgive you—He gave you a new heart. Not a reformed heart. Not a patched-up heart. A brand NEW heart. You don\'t have a "wicked heart" to manage. You have a heart that wants what God wants. Your deepest desires, at the core, are good.'
  },
  {
    title: 'You Are a New Creation',
    scripture: 'Therefore, if anyone is in Christ, he is a new creation. The old has passed away; behold, the new has come.',
    reference: '2 Corinthians 5:17 ESV',
    explanation: 'You are not the "old you" trying to be better. The old has PASSED AWAY. You are NEW. This isn\'t positive thinking—it\'s your actual identity. Sin is not the real you; the new creation is the real you.'
  },
  {
    title: 'Sin Is Not Your True Self',
    scripture: 'So now it is no longer I who do it, but sin that dwells within me.',
    reference: 'Romans 7:17, 20 ESV',
    explanation: 'When you sin, it\'s not the "real you" expressing itself. Paul says clearly: "It is no longer I... but sin that dwells within me." Sin is like a parasite—it\'s IN you but it\'s not YOU. Your true identity is righteous, holy, and new in Christ.'
  },
  {
    title: 'You Are Permanently United',
    scripture: 'But he who is joined to the Lord becomes one spirit with him.',
    reference: '1 Corinthians 6:17 ESV',
    explanation: 'You are ONE SPIRIT with the Lord. Not two. Not sometimes connected, sometimes distant. ONE. This union cannot be broken by sin, feelings, or failure. You cannot be "out of fellowship" with someone you\'re permanently fused with.'
  },
  {
    title: 'You Are Complete in Him',
    scripture: 'And you have been filled in him, who is the head of all rule and authority.',
    reference: 'Colossians 2:10 ESV',
    explanation: 'You are not lacking. Not incomplete. Not working toward wholeness. You HAVE BEEN FILLED—complete, right now, in Christ. Stop striving for what you already possess.'
  },
  {
    title: 'You Are Sealed Forever',
    scripture: 'In him you also, when you heard the word of truth, the gospel of your salvation, and believed in him, were sealed with the promised Holy Spirit, who is the guarantee of our inheritance.',
    reference: 'Ephesians 1:13-14 ESV',
    explanation: 'You are SEALED. The Holy Spirit is God\'s guarantee—His down payment—that your salvation is secure. This seal cannot be broken. Your security rests on God\'s faithfulness, not your performance.'
  },
  {
    title: 'You Are Perfected Forever',
    scripture: 'For by a single offering he has perfected for all time those who are being sanctified.',
    reference: 'Hebrews 10:14 ESV',
    explanation: 'You are already PERFECTED—for all time. Not working toward perfection. Already there, in God\'s eyes. Daily sanctification is learning to live from this reality, not earning it.'
  },
  {
    title: 'You Are Totally Forgiven',
    scripture: 'And you, who were dead in your trespasses... God made alive together with him, having forgiven us all our trespasses.',
    reference: 'Colossians 2:13 ESV',
    explanation: 'ALL your trespasses. Past. Present. Future. All forgiven at the cross. You never need to ask God to forgive you again—you simply thank Him that you already ARE forgiven.'
  },
];

const journalPrompts = [
  "What does being 100% forgiven—past, present, and future—change about how I approach today?",
  "Where have I been striving when I could be resting in Christ's finished work?",
  "How is grace training me today, rather than guilt pressuring me? (Titus 2:11-12)",
  "What would change if I truly believed I could never be 'out of fellowship' with God?",
  "When I sinned recently, did I ask for forgiveness or thank God I'm already forgiven?",
  "How does knowing sin is 'not the real me' (Romans 7:17) change how I see my struggles?",
  "What does it mean that I'm COMPLETE in Christ right now? (Colossians 2:10)"
];

export default function IdentityHub() {
  const [scriptureCards, setScriptureCards] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCards = async () => {
      try {
        const response = await axios.get(`${API}/scripture-cards`);
        setScriptureCards(response.data);
      } catch (error) {
        console.error('Failed to fetch scripture cards:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCards();
  }, []);

  const handlePrevCard = () => {
    setCurrentCardIndex((prev) => 
      prev === 0 ? scriptureCards.length - 1 : prev - 1
    );
  };

  const handleNextCard = () => {
    setCurrentCardIndex((prev) => 
      prev === scriptureCards.length - 1 ? 0 : prev + 1
    );
  };

  const handleRandomCard = () => {
    const randomIndex = Math.floor(Math.random() * scriptureCards.length);
    setCurrentCardIndex(randomIndex);
  };

  return (
    <div className="min-h-[calc(100vh-64px)]" data-testid="identity-hub">
      {/* Hero Section */}
      <section className="relative h-64 sm:h-80 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1598044520548-c1b8ef293798?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Peaceful green trees and water representing reflection"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hub-hero-overlay flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 w-full">
            <span className="inline-block px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm mb-3">
              Step 2 · Identity
            </span>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              Identity Center
            </h1>
            <p className="text-white/80 mt-2 max-w-2xl">
              New heart, new creation, complete in Christ
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Union Meter */}
        <Card className="mb-8 bg-primary/5 border-primary/20">
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Your Union with Christ
            </CardTitle>
            <CardDescription>
              This never fluctuates. You are permanently, completely united.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Progress value={100} className="flex-1 h-3" />
              <span className="text-2xl font-bold text-primary">100%</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2 italic">
              "He who is joined to the Lord becomes one spirit with him." — 1 Corinthians 6:17
            </p>
          </CardContent>
        </Card>

        <Tabs defaultValue="truths" className="space-y-8">
          <TabsList className="grid w-full max-w-lg grid-cols-4">
            <TabsTrigger value="truths" data-testid="tab-truths">Truths</TabsTrigger>
            <TabsTrigger value="cards" data-testid="tab-cards">Daily Cards</TabsTrigger>
            <TabsTrigger value="journal" data-testid="tab-journal">Journal</TabsTrigger>
            <TabsTrigger value="quiz" data-testid="tab-quiz">Quiz</TabsTrigger>
          </TabsList>

          <TabsContent value="truths" className="space-y-8">
            <div className="max-w-3xl">
              <p className="text-lg text-muted-foreground leading-relaxed">
                Your identity is not your behavior. It's not your feelings. It's who God says you are 
                in Christ. And in Christ, you are NEW—with a new heart, complete, united, and forgiven. 
                These truths don't fluctuate with your performance.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {identityTruths.map((truth, index) => (
                <Card key={truth.title} className="animate-slide-up" style={{ animationDelay: `${index * 50}ms` }}>
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Heart className="h-5 w-5 text-primary" strokeWidth={1.5} />
                      <CardTitle className="font-serif text-lg">{truth.title}</CardTitle>
                    </div>
                    <CardDescription className="italic">
                      "{truth.scripture}"
                    </CardDescription>
                    <p className="text-sm text-primary font-medium">— {truth.reference}</p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-foreground/80 leading-relaxed">
                      {truth.explanation}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="cards" className="space-y-6">
            <div className="max-w-3xl">
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                These Scripture cards remind you of who you ARE in Christ—not who you're trying to become. 
                Your identity is settled. Let these truths renew your mind.
              </p>
            </div>

            {loading ? (
              <div className="max-w-xl mx-auto">
                <Card className="animate-pulse">
                  <CardContent className="p-8">
                    <div className="h-6 bg-muted rounded w-1/4 mb-4"></div>
                    <div className="h-20 bg-muted rounded mb-4"></div>
                    <div className="h-4 bg-muted rounded w-1/3 mb-4"></div>
                    <div className="h-16 bg-muted rounded"></div>
                  </CardContent>
                </Card>
              </div>
            ) : scriptureCards.length > 0 ? (
              <div className="max-w-xl mx-auto space-y-4">
                <ScriptureCard card={scriptureCards[currentCardIndex]} />
                
                <div className="flex items-center justify-center gap-4">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={handlePrevCard}
                    aria-label="Previous card"
                    data-testid="card-prev"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
                  <span className="text-sm text-muted-foreground">
                    {currentCardIndex + 1} of {scriptureCards.length}
                  </span>
                  
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={handleNextCard}
                    aria-label="Next card"
                    data-testid="card-next"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={handleRandomCard}
                    className="ml-4"
                    data-testid="card-random"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Random
                  </Button>
                </div>
              </div>
            ) : (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  No scripture cards available
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="journal">
            <div className="max-w-3xl mx-auto">
              <div className="mb-8">
                <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                  Use this journal to explore what these truths mean for YOUR life. Growth happens 
                  through renewing the mind (Romans 12:2)—not fixing your heart (it's already new!).
                </p>
                
                {/* Journal Prompts */}
                <Card className="bg-muted/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Grace-Centered Journal Prompts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {journalPrompts.slice(0, 4).map((prompt, i) => (
                        <li key={i} className="text-sm text-muted-foreground flex gap-2">
                          <span className="text-primary">•</span>
                          {prompt}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
              <JournalSection />
            </div>
          </TabsContent>

          <TabsContent value="quiz">
            <div className="max-w-2xl mx-auto">
              <QuizComponent hubId="identity" />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
